import React, { useCallback, useContext, useEffect, useState } from 'react';

import { Row, Col } from 'antd';
import { Field, Form, Formik } from 'formik';

// ROUTES
import { useHistory } from 'react-router-dom';
import {
	ROUTE_AAC_PESQUISA_ANTECEDENTES_NUMERAL,
	ROUTE_AAC_PESQUISA_CENTRAL_NUMERAL,
} from 'pages/iirgd/Aac/routes/paths';

// REDUX
import { useSelector, useDispatch } from 'react-redux';
import { ApplicationState } from 'store';
import { limpaPesquisaCITAAAC } from 'store/modules/api/aac/pesquisaCITA/actions';
import {
	emissaoAtestadoAntecedentesRequest,
	limpaAtestadoAntecedentes,
} from 'store/modules/api/aac/emissaoAtestadoAntecedentes/actions';
import data from 'store/modules/api/iirgd/enum';
import {
	eventosPPTContabilizacaoClear,
	eventosPPTContabilizacaoRequest,
} from 'store/modules/api/utils/eventosPPT/contabilizacao/actions';
import { EventosPPTContabilizacaoRequest } from 'store/modules/api/utils/eventosPPT/contabilizacao/types';
import { allowedAACErrorCodes } from 'store/modules/api/aac/emissaoAtestadoAntecedentes/saga';
import { consultaCpfAacClear } from 'store/modules/api/aac/consutaCpf/actions';

// COMPONENTS
import Section from 'components/Common/Section';
import Input from 'components/Common/Form/Input/Input';
import InputMask from 'components/Common/Form/Input/InputMask';
import ButtonImage from 'components/Common/ButtonImage';
import Select from 'components/Common/Form/Select';
import ButtonVoltar from 'components/Common/ButtonVoltar';
import ValidDataInput from 'components/Common/Form/Input/ValidData';
import Divider from 'components/Common/Divider';

// UTILS
import getValidationsErrors from 'utils/getValidationsErrors';
import hasError from 'utils/getFormErrors';
import handleFailed from 'utils/getValidations';
import {
	convertDate,
	formatDate,
	limparMascara,
	extrairNumeroDigito,
	verificarMaioridade,
	unformatDate,
} from 'utils/genericFunctions';
import { treatRequestEventsContabilizacaoPPT } from 'utils/functions/treatEventosPPTRequest';

// FORM
import { comparaData } from 'utils/dateFunctions';
import {
	addNotifications,
	clearNotifications,
} from 'store/modules/app/notifications/actions';
import { RequestEmissaoAtestadoAntecedentes } from 'store/modules/api/aac/emissaoAtestadoAntecedentes/types';
import { AuthContext, IAuthContext } from 'react-oauth2-code-pkce';
import { baixarArquivosClear } from 'store/modules/api/utils/arquivos/baixarArquivos/actions';
import PDFViewer from 'components/Common/PDFViewer';
import NavMenu from '../../components/NavMenu';
import { FormEmissaoNumeral, schema } from './form';

// STYLED
import { ContentButton } from '../Consultar/styled';
import Procurador from '../../components/Procurador';

const NumeralResultado: React.FC = () => {
	const dispatch = useDispatch();
	const history = useHistory();

	const { pesquisaCitaAAC, emissaoAtestadoAntecedentes, consultaCpf } =
		useSelector((state: ApplicationState) => state.api.aac);

	const [protocoloBase64, setProtocoloBase64] = useState<string>('');

	const { login } = useSelector(
		(state: ApplicationState) => state.api.sgu.loginUnico,
	);

	const { baixarArquivos, eventosPPTContabilizacao } = useSelector(
		(state: ApplicationState) => state.api.utils,
	);

	const { loginUnico, atendimento } = useSelector(
		(state: ApplicationState) => state.api.sgu,
	);

	const { errors: notificationErros } = useSelector(
		(state: ApplicationState) => state.app.notifications,
	);

	const { token } = useContext<IAuthContext>(AuthContext);

	const initialValues: FormEmissaoNumeral = {
		numeroRg: pesquisaCitaAAC.data.numeroRg,
		digitoRg: '',
		dataExpedicaoRg: '',
		nomeSocial: pesquisaCitaAAC.data.nomeSocial,
		nome: pesquisaCitaAAC.data.nome,
		nomePai: pesquisaCitaAAC.data.nomePai,
		nomeMae: pesquisaCitaAAC.data.nomeMae,
		sexo: pesquisaCitaAAC.data.sexo,
		dataNascimento: formatDate(pesquisaCitaAAC.data.dataNascimento),
		requerente: false,
		nomeProcurador: '',
		docProcurador: '',
		posto: login.user.posto,
		ipEstacao: login.user.ip,
		usuario: login.mainframe.idUsuario,
		numeroDocumento: '',
		origem: 'BU',
		tipoEmissao: 'NUMERAL',
		cpf: consultaCpf.data?.cpf
			? `000000000000${Number(
					`${consultaCpf.data?.cpf}${consultaCpf.data?.dcCpf}`,
			  )}`.slice(-11)
			: '',
		tipoDocApresentado: consultaCpf.data?.cpf ? 'CIN' : 'RG',
	};

	const [dataExpedicao, setDataExpedicao] = useState<string>('');
	const [dataInvalida, setDataInvalida] = useState<boolean>(true);

	useEffect(() => {
		if (dataInvalida && limparMascara(dataExpedicao).length === 8) {
			addNotifications({
				errors: [
					'A data de expedição não pode ser anterior a data de nascimento.',
				],
			});
		}
	}, [dataExpedicao, dataInvalida]);

	const validaDataExpedicao = useCallback(
		(values: string): void => {
			if (limparMascara(values).length < 8) {
				return setDataInvalida(true);
			}
			if (
				pesquisaCitaAAC.data.dataNascimento &&
				values &&
				limparMascara(values).length === 8
			) {
				if (
					!comparaData(
						values,
						pesquisaCitaAAC.data.dataNascimento,
						'maiorIgual',
					)
				) {
					return setDataInvalida(
						!comparaData(
							values,
							pesquisaCitaAAC.data.dataNascimento,
							'maiorIgual',
						),
					);
				}
				dispatch(clearNotifications());

				return setDataInvalida(
					!comparaData(
						values,
						pesquisaCitaAAC.data.dataNascimento,
						'maiorIgual',
					),
				);
			}

			return setDataInvalida(
				!comparaData(values, pesquisaCitaAAC.data.dataNascimento, 'maiorIgual'),
			);
		},
		[dispatch, pesquisaCitaAAC.data.dataNascimento],
	);

	useEffect(() => {
		validaDataExpedicao(dataExpedicao);
	}, [dataExpedicao, validaDataExpedicao]);

	const handleSubmitForm = useCallback(
		(values: FormEmissaoNumeral) => {
			if (values.dataNascimento) {
				if (!verificarMaioridade(values.dataNascimento)) {
					addNotifications({
						errors: [
							'Atenção, a emissão de atestado só é permitida para maiores de 18 anos!',
						],
					});
				} else {
					const formData: RequestEmissaoAtestadoAntecedentes = {
						dataNascimento: `${unformatDate(values.dataNascimento)}`,
						dataExpedicaoRg: convertDate(values.dataExpedicaoRg),
						nomeSocial: values.nomeSocial,
						nome: values.nome,
						nomeMae: values.nomeMae === 'NC' ? '' : values.nomeMae,
						nomePai: values.nomePai === 'NC' ? '' : values.nomePai,
						numeroDocumento: '',
						numeroRg:
							values.tipoDocApresentado.toLocaleUpperCase() !== 'CIN'
								? Number(extrairNumeroDigito(values.numeroRg, 'num'))
								: Number(consultaCpf.data?.numeroRg) || '',
						digitoRg:
							values.tipoDocApresentado.toLocaleUpperCase() !== 'CIN'
								? extrairNumeroDigito(values.numeroRg, 'dig')
								: consultaCpf.data?.dcRg || '',
						sexo: values.sexo === 'F' ? 'FEMININO' : 'MASCULINO',
						ipEstacao: login.user.ip,
						posto: login.user.posto,
						tipoDocApresentado: values.tipoDocApresentado,
						usuario: login.mainframe.idUsuario,
						nomeProcurador: values.nomeProcurador
							? values.nomeProcurador.toUpperCase()
							: '',
						docProcurador: values.docProcurador
							? values.docProcurador.toUpperCase()
							: '',
						tipoEmissao: 'NUMERAL',
						origem: 'BU',
						cpf:
							values.tipoDocApresentado.toLocaleUpperCase() === 'CIN' &&
							pesquisaCitaAAC.data.cpf
								? `${extrairNumeroDigito(pesquisaCitaAAC.data.cpf, 'num')}`
								: '',
						dcCpf:
							values.tipoDocApresentado.toLocaleUpperCase() === 'CIN' &&
							pesquisaCitaAAC.data.cpf
								? `${extrairNumeroDigito(pesquisaCitaAAC.data.cpf, 'dig')}`
								: '',
					};
					dispatch(emissaoAtestadoAntecedentesRequest(token, formData));
				}
			}
		},
		[
			consultaCpf.data,
			dispatch,
			login.mainframe.idUsuario,
			login.user.ip,
			login.user.posto,
			pesquisaCitaAAC.data.cpf,
			token,
		],
	);

	useEffect(() => {
		if (
			emissaoAtestadoAntecedentes.status !== 200 &&
			emissaoAtestadoAntecedentes.data.mensagem
		) {
			handleFailed({
				mensagem_erro: emissaoAtestadoAntecedentes.data.mensagem,
			});
		} else if (
			emissaoAtestadoAntecedentes.status === 200 &&
			baixarArquivos.status === 200 &&
			emissaoAtestadoAntecedentes.data.pdfDataUrl &&
			emissaoAtestadoAntecedentes.data.pdfDataUrl !== '' &&
			baixarArquivos.base64
		) {
			setProtocoloBase64(baixarArquivos.base64);
			dispatch(limpaAtestadoAntecedentes());
		} else if (
			(emissaoAtestadoAntecedentes.status === 200 &&
				baixarArquivos.status === 200) ||
			(emissaoAtestadoAntecedentes.status === 200 &&
				emissaoAtestadoAntecedentes.data?.codigo &&
				allowedAACErrorCodes.includes(emissaoAtestadoAntecedentes.data.codigo))
		) {
			history.push(ROUTE_AAC_PESQUISA_CENTRAL_NUMERAL);
		} else if (pesquisaCitaAAC.status === 0) {
			history.push(ROUTE_AAC_PESQUISA_ANTECEDENTES_NUMERAL);
		}
	}, [
		emissaoAtestadoAntecedentes,
		history,
		dispatch,
		pesquisaCitaAAC,
		baixarArquivos,
	]);

	const handleContabilizacacao = useCallback(() => {
		const payload: EventosPPTContabilizacaoRequest =
			treatRequestEventsContabilizacaoPPT(
				atendimento,
				loginUnico,
				'',
				'IIRGD',
				396,
				'atestado antecedentes',
				2,
				'iirgd_atestado_antecedentes',
			);
		dispatch(eventosPPTContabilizacaoRequest(token, payload));
	}, [atendimento, loginUnico, dispatch, token]);

	useEffect(() => {
		if (
			emissaoAtestadoAntecedentes.status === 200 &&
			(!emissaoAtestadoAntecedentes.data?.codigo ||
				(emissaoAtestadoAntecedentes.data?.codigo &&
					!allowedAACErrorCodes.includes(
						emissaoAtestadoAntecedentes.data.codigo,
					))) &&
			eventosPPTContabilizacao.status === 0
		) {
			handleContabilizacacao();
		}
	}, [
		dispatch,
		emissaoAtestadoAntecedentes,
		eventosPPTContabilizacao.status,
		handleContabilizacacao,
		token,
	]);

	const renderTipoDocOptions = (
		options: { label: string; value: string }[],
		isCin: boolean,
	) => {
		if (!isCin) {
			return options.filter(opt => opt.value !== 'CIN');
		}
		return options;
	};

	return (
		<>
			<Row>
				<Col span={24}>
					<NavMenu />

					<Divider mtSize={15} mbSize={15} />
				</Col>
			</Row>
			<Section title="Resultado ">
				<Formik
					validateOnChange={!!notificationErros.length}
					validateOnBlur={!!notificationErros.length}
					validate={values => getValidationsErrors(values, schema)}
					initialValues={initialValues}
					onSubmit={handleSubmitForm}
				>
					{formik => {
						const { errors, setFieldValue } = formik;
						return (
							<Form autoComplete="off">
								<Row gutter={[0, 10]}>
									<Col span={8}>
										{consultaCpf.data?.cpf ? (
											<Field
												name="cpf"
												title="Nº da CIN de SP (CPF)"
												type="text"
												mask="999.999.999-99"
												maskChar=""
												disabled
												as={InputMask}
											/>
										) : (
											<Field
												name="numeroRg"
												title="RG de SP"
												type="text"
												mask="99.999.999-**"
												maskChar=""
												disabled
												as={InputMask}
											/>
										)}
									</Col>
									<Col span={6}>
										<Field
											as={Select}
											name="tipoDocApresentado"
											type="text"
											title="Doc. Apresentado"
											onChange={(v: number) =>
												setFieldValue('tipoDocApresentado', v)
											}
											options={renderTipoDocOptions(
												data.docApresentado,
												!!consultaCpf.data?.cpf,
											)}
											disabled={!!consultaCpf.data?.cpf}
										/>
									</Col>
									<Col span={9} offset={1}>
										<Field
											id="dataExpedicaoRg"
											title="Data de Expedição"
											name="dataExpedicaoRg"
											type="text"
											mask="99/99/9999"
											subtitle="(DD/MM/AAAA)"
											size={40}
											as={ValidDataInput}
											error={hasError(errors, 'dataExpedicaoRg')}
											formik={formik}
											customChanges={(
												v: React.ChangeEvent<HTMLInputElement>,
											) => {
												setDataExpedicao(v.currentTarget.value);
												setFieldValue('dataExpedicaoRg', v.currentTarget.value);
											}}
										/>
									</Col>
									<Col span={24}>
										<Field
											title="Nome"
											name="nome"
											size={80}
											as={Input}
											disabled
										/>
									</Col>
									<Col span={24}>
										<Field
											title="Filiação 1"
											name="nomePai"
											size={80}
											as={Input}
											disabled
										/>
									</Col>
									<Col span={24}>
										<Field
											title="Filiação 2"
											name="nomeMae"
											size={80}
											as={Input}
											disabled
										/>
									</Col>
									<Col span={8}>
										<Field
											as={Select}
											name="sexo"
											type="text"
											title="Sexo"
											size={85}
											options={data.sexo}
											disabled
										/>
									</Col>
									<Col span={16}>
										<Field
											title="Data de Nascimento"
											name="dataNascimento"
											type="text"
											mask="99/99/9999"
											subtitle="(DD/MM/AAAA)"
											size={25}
											as={InputMask}
											disabled
										/>
									</Col>
									<Procurador formik={formik} />
								</Row>
								{!dataInvalida && (
									<ContentButton justify="start">
										<ButtonImage type="submit" src="imprimir" />
									</ContentButton>
								)}
							</Form>
						);
					}}
				</Formik>
			</Section>
			{protocoloBase64 && (
				<PDFViewer
					title="Atestado de antecedentes"
					source={protocoloBase64}
					onClose={() => {
						setProtocoloBase64('');
						dispatch(baixarArquivosClear());
						dispatch(eventosPPTContabilizacaoClear());
					}}
				/>
			)}
			<ButtonVoltar
				navigate={false}
				onClick={() => {
					dispatch(limpaPesquisaCITAAAC());
					dispatch(consultaCpfAacClear());
					history.push(
						'/iirgd/pesquisa-antecedentes/numeral/pesquisa-antecedentes',
					);
				}}
			/>
		</>
	);
};

export default NumeralResultado;
